import { useEffect } from 'react';
import type { PageJson } from '../../CardCustomizer.types';
import type { TextLayerData } from '../../components/Layer.types';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGetEventPrivacyForCardCustomizerQuery } from '../../CardCustomizer.graphql.generated';
import { TEMPLATE_PLACEHOLDER_LINK } from '../../CardCustomizer.constants';
import { isJoyHost, isJoyWebsiteUrl } from '@shared/utils/guestSiteUrl';
import { PASSWORD_LAYER_NAME, isPasswordDisplayContent, buildPasswordLine, syncPasswordDisplayContent } from '../../components/passwordDisplayLayer';

/**
 * Inputs needed to populate stationery event-link layers. Extracted from the hook so the
 * logic below can be unit-tested without the React/GraphQL machinery.
 */
export type PopulateEventLinkContext = Readonly<{
  eventHandle: string;
  env: string; // 'dev.' for local/dev, '' for prod
  shouldShowPassword: boolean;
  eventPassword: string;
}>;

/**
 * Try to parse a URL, accepting both protocol-prefixed (`https://withjoy.com/...`) and
 * protocol-less (`withjoy.com/...`) values. Stationery draft content may omit the protocol
 * (the template placeholder is `withjoy.com/name-and-name`), and some legacy drafts store
 * URLs without a scheme — both need to be recognised.
 */
const tryParseUrl = (candidate: string | undefined): URL | null => {
  if (!candidate) return null;
  try {
    return new URL(candidate);
  } catch {
    try {
      return new URL(`https://${candidate}`);
    } catch {
      return null;
    }
  }
};

/**
 * Populates / normalises event-link layers on a stationery draft in place.
 *
 * Text layer (`layer.name === 'URL'`):
 *   - Empty → fill with the bare current-event URL
 *   - Stale joy URL (different handle) → overwrite with current-event URL
 *   - Correct-handle joy URL (with or without UTMs, any subpath like `/{handle}/rsvp`) → preserve
 *   - Custom third-party URL → preserve
 *
 * QR layer:
 *   - Placeholder → fill with the bare current-event URL
 *   - Stale joy URL → overwrite with bare current-event URL
 *   - Correct-handle joy URL (any subpath) → preserve
 *   - Custom third-party URL → preserve
 *   - Password: attached to any joy URL, stripped for lookalike hosts. Re-read after any
 *     overwrite so the decision reflects the effective link, not a stale pre-overwrite value.
 *
 * NOTE: UTM params are NOT persisted in the draft JSON. QRCodeLayer.tsx adds its own tracking
 * params at render time when generating the QR code image. Keeping the saved link clean prevents
 * downstream renderers (like card_service) from inadvertently printing UTM params in the visible
 * URL text on the back of the card.
 *
 * Exported so `usePopulateEventLink.spec.ts` can cover the heuristic directly.
 */
export const populateEventLinkInDraft = (draft: PageJson, ctx: PopulateEventLinkContext): void => {
  const { eventHandle, env, shouldShowPassword, eventPassword } = ctx;

  // Both text and QR layers store the bare URL. QRCodeLayer.tsx adds UTM params at render time
  // when generating the QR image — the saved draft stays clean so card_service (and any other
  // downstream consumer) never prints tracking params in the human-readable URL text.
  const eventLink = `https://${env}withjoy.com/${eventHandle}`;

  /** True if `candidate` points at this event (matches `/{handle}` or any `/{handle}/...` subpath). */
  const isUrlForThisEvent = (candidate: string | undefined): boolean => {
    const parsed = tryParseUrl(candidate);
    if (!parsed || !isJoyHost(parsed.hostname)) return false;
    return parsed.pathname === `/${eventHandle}` || parsed.pathname.startsWith(`/${eventHandle}/`);
  };

  /**
   * True if `candidate` is a Contact Collector link (a joy `/contact/...` URL). It's an intentional
   * link the host chose in the editor, so it must be preserved — not treated as a stale event link
   * and overwritten with the event website URL.
   */
  const isContactCollectorUrl = (candidate: string | undefined): boolean => {
    const parsed = tryParseUrl(candidate);
    return !!parsed && isJoyHost(parsed.hostname) && parsed.pathname.startsWith('/contact/');
  };

  /** True if `candidate` is a joy URL but not for this event (stale after handle rename). */
  const isStaleJoyUrl = (candidate: string | undefined): boolean => {
    const parsed = tryParseUrl(candidate);
    if (!parsed) return false;
    return isJoyHost(parsed.hostname) && !isUrlForThisEvent(candidate) && !isContactCollectorUrl(candidate);
  };

  // The card shows a Joy password only when its website link points at the event's Joy site — a
  // custom/external link, or a Contact Collector (`/contact/…`) magic link, carries no Joy password
  // (matching the QR flow and WebsiteLink.handleSave). A placeholder or stale-handle joy link is
  // still the event site, so it counts. Absent a URL layer, fall back to the event's own privacy.
  const urlLayer = draft.layers.find((l): l is TextLayerData => l.type === 'text' && l.name === 'URL');
  const urlContent = urlLayer?.textData.content;
  const parsedUrlLink = urlLayer ? tryParseUrl(urlContent) : null;
  const linkIsJoyWebsite = urlLayer ? !!parsedUrlLink && isJoyHost(parsedUrlLink.hostname) && !isContactCollectorUrl(urlContent) : true;
  const showCardPassword = shouldShowPassword && linkIsJoyWebsite && !!eventPassword;

  for (const layer of draft.layers) {
    if (layer.type === 'text' && layer.name === 'URL') {
      const current = layer.textData.content;
      if (current == null || current === TEMPLATE_PLACEHOLDER_LINK || isStaleJoyUrl(current)) {
        layer.textData.content = eventLink;
      }
      // else: correct-handle joy URL or custom third-party URL — preserve.
    }

    // Password display layer. Stamp it once (client-side, saved with the draft — no template change)
    // so it stays controllable after its text is cleared. Show "Password: <pw>" only for a private
    // event on a Joy website; otherwise clear the line so no stale or irrelevant password (e.g. next
    // to a custom link, mirroring the QR flow) is printed. Editing the password / link re-runs this,
    // so the card and saved draft update together.
    if (layer.type === 'text') {
      if (layer.name == null && isPasswordDisplayContent(layer.textData.content)) {
        layer.name = PASSWORD_LAYER_NAME;
      }
      if (layer.name === PASSWORD_LAYER_NAME) {
        let nextContent: string;
        if (!showCardPassword) {
          nextContent = '';
        } else if (isPasswordDisplayContent(layer.textData.content)) {
          // Still a "Password: …" line — keep the template's original label, swap the value.
          nextContent = syncPasswordDisplayContent(layer.textData.content, eventPassword);
        } else {
          // Previously hidden (empty) — rebuild the line with the canonical label.
          nextContent = buildPasswordLine(eventPassword);
        }
        if (nextContent !== layer.textData.content) {
          layer.textData.content = nextContent;
        }
      }
    }
    if (layer.type === 'qrcode') {
      const currentBefore = layer.qrcodeData.link;
      const hasPlaceholder = !currentBefore || currentBefore === TEMPLATE_PLACEHOLDER_LINK;
      if (hasPlaceholder || isStaleJoyUrl(currentBefore)) {
        layer.qrcodeData.link = eventLink;
      }
      // Re-read after the conditional overwrite — the password decision must reflect the
      // effective link (post-overwrite), not the pre-overwrite placeholder / stale value.
      // Password attaches to the event's joy website; lookalike hosts and Contact Collector
      // (`/contact/…`) magic links get none (the latter is public, so printing the event password
      // beside it would leak private-site access).
      const effectiveLink = layer.qrcodeData.link;
      const isJoyEventLink = !!effectiveLink && isJoyWebsiteUrl(effectiveLink) && !isContactCollectorUrl(effectiveLink);
      layer.qrcodeData.password = isJoyEventLink && shouldShowPassword ? eventPassword : undefined;
    }
  }
};

export const usePopulateEventLink = (onPrivacyDataChange: (cb: (draft: PageJson) => void) => void) => {
  const { eventInfo, eventHandle } = useEventInfo();

  const { data: privacyData } = useGetEventPrivacyForCardCustomizerQuery({
    variables: {
      eventId: eventInfo?.eventId ?? '',
    },
    batchMode: 'fast',
    skip: !eventInfo?.eventId,
  });

  const populateEventLink = (draft: PageJson) => {
    if (!privacyData) return;
    populateEventLinkInDraft(draft, {
      eventHandle: eventHandle ?? '',
      env: process.env.NODE_ENV === 'development' ? 'dev.' : '',
      shouldShowPassword: privacyData.eventById?.settings.privacyMode !== 'public',
      eventPassword: privacyData.eventById?.info.eventPassword || '',
    });
  };

  useEffect(() => {
    onPrivacyDataChange(populateEventLink);
    // Disabling-next-line because `update` is not stable (larger issue),
    // however the stale `update` still works.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privacyData]);

  return { populateEventLink };
};
